import { Injectable } from '@angular/core';
import * as braze from '@braze/web-sdk';
import { UserInfo } from '../../app/auth/models/user';
import { environment } from 'src/environments/environment';
import { SignupRequest } from '../auth/models/signup';
import { getCompanyType } from '../utils/companyType';

@Injectable({
  providedIn: 'root',
})
export class BrazeService {
  initBraze() {
    braze.initialize(environment.braze.apiKey, {
      baseUrl: environment.braze.baseUrl,
      // 1 hour to match SSO token expiration. Sessions time out after 1 hour of inactivity
      sessionTimeoutInSeconds: 3600,
      minimumIntervalBetweenTriggerActionsInSeconds:
        environment.braze.minimumIntervalBetweenTriggerActionsInSeconds,
      enableLogging: environment.braze.enableLogging,
    });
    braze.automaticallyShowInAppMessages();
    braze.openSession();
  }

  setAttr(name: string, value: string | boolean | number): void {
    braze.getUser().setCustomUserAttribute(name, value);
  }

  delAttr(name: string): void {
    braze.getUser().setCustomUserAttribute(name, null);
  }

  uniqueId({
    userName,
    account,
  }: {
    userName: string;
    account: string;
  }): string {
    return `${userName}@${account}`;
  }

  track(event: string) {
    braze.logCustomEvent(event);
  }

  trackLogin(user: UserInfo): void {
    // braze analytics for login
    braze.changeUser(this.uniqueId(user));
    braze.getUser().setFirstName(user.firstName);
    braze.getUser().setLastName(user.lastName);
    braze.getUser().setEmail(user.userName);
    const featureLevel =
      user.accountType === 'Trial' ? 'Trial' : user.subscriptionLevel;
    this.setAttr('Subscription Level', featureLevel);
    if (featureLevel === 'Trial') {
      braze.getUser().setCustomUserAttribute('Trial End', user.expirationDate);
      const remainingRoutes =
        user.remainingRoutes < 0 ? null : user.remainingRoutes;
      this.trackRemainingRoutes(remainingRoutes);
    }
    this.setAttr('Has Used Modern', true);
    this.setAttr('Last Used Version', 'V2');
  }
  trackRemainingRoutes(remainingRoutes: number) {
    const remaining = remainingRoutes < 0 ? null : remainingRoutes;
    braze
      .getUser()
      .setCustomUserAttribute('Routes Remaining (Trial User)', remaining);
  }

  trackSignup(signup: SignupRequest): void {
    // braze analytics for sign up
    braze.changeUser(
      this.uniqueId({ userName: signup.userName, account: signup.companyName })
    );
    braze.getUser().setFirstName(signup.firstName);
    braze.getUser().setLastName(signup.lastName);
    // username should be an email address
    braze.getUser().setEmail(signup.userName);
    const featureLevel = signup.featureLevel;
    if (featureLevel === 'Trial') {
      braze.logCustomEvent('Trial Activation');
      braze.getUser().setCustomUserAttribute('Trial Start', new Date());
    } else if (featureLevel === 'Premium') {
      braze.logCustomEvent('Premium Sub Purchase');
      braze
        .getUser()
        .setCustomUserAttribute('Premium Subscription Start', new Date());
    }
    braze.getUser().setCustomUserAttribute('Subscription Level', featureLevel);
    braze.getUser().setHomeCity(`${signup.city}, ${signup.state}`);
    braze.getUser().setCustomUserAttribute('User City', signup.city);
    braze.getUser().setCustomUserAttribute('User State', signup.state);
    braze
      .getUser()
      .setCustomUserAttribute('number_of_trucks', signup.numberOfTrucks);
    braze.getUser().setCustomUserAttribute('company_name', signup.companyName);
    const companyType = getCompanyType(signup.companyType);
    braze.getUser().setCustomUserAttribute('company_type', companyType);
    // switch back to BrazeDummyUser because user still has to sign in after signing up
    this.trackLogout();
  }

  trackCancelAcc(featureLevel: string = 'Trial'): void {
    // braze analytics for canceling account
    // The app only supports trial and premium subs.
    if (featureLevel === 'Trial') {
      braze.logCustomEvent('Trial Cancelled');
    } else {
      braze.logCustomEvent('Premium Sub Cancelled');
    }
    braze.getUser().setCustomUserAttribute('Deactivation Date', new Date());
  }

  trackUpdateBilling({
    ogCity,
    newCity,
    ogState,
    newState,
  }: {
    ogCity: string;
    newCity: string;
    ogState: string;
    newState: string;
  }): void {
    // braze analytics for updating account billing features
    if (ogCity !== newCity || ogState !== newState) {
      if (ogCity !== newCity) {
        braze.getUser().setCustomUserAttribute('User City', newCity);
      }
      if (ogState !== newState) {
        braze.getUser().setCustomUserAttribute('User State', newState);
      }
      braze.getUser().setHomeCity(`${newCity}, ${newState}`);
    }
  }

  trackUpdateSubLevel({ ogFL, newFL }: { ogFL: string; newFL: string }): void {
    // braze analytics for updating account feature level
    // pchen: some checks in the if conditions might be redundant based on where this function is called
    if (ogFL === 'Trial') {
      braze.logCustomEvent(`${newFL} Sub Purchase`);
    } else if (ogFL === 'Standard' && newFL === 'Premium') {
      braze.logCustomEvent('Standard to Premium Upgrade');
    } else if (ogFL === 'Premium' && newFL === 'Standard') {
      braze.logCustomEvent('Premium to Standard Downgrade');
    }
    braze.getUser().setCustomUserAttribute('Subscription Level', newFL);
    if (newFL !== 'Trial') {
      braze
        .getUser()
        .setCustomUserAttribute(`${newFL} Subscription Start`, new Date());
    }
  }

  trackAppChange(product: { name: string }): void {
    // braze diagnostics for cloud menu
    let brazeEventName = 'Clicks to ';
    switch (product.name) {
      case 'Content Tools':
      case 'MapSure':
        brazeEventName += product.name;
        break;
      default:
        brazeEventName = null;
    }
    if (brazeEventName) {
      braze.logCustomEvent(brazeEventName);
    }
  }

  trackRouteOptions(options: {
    useTraffic: boolean;
    afSetIDs: [number];
  }): void {
    // braze analytics for route options
    if (options.useTraffic) {
      braze.logCustomEvent('Use Traffic');
      braze.getUser().setCustomUserAttribute('Traffic', true);
    } else {
      braze.getUser().setCustomUserAttribute('Traffic', null);
    }
    if (options.afSetIDs && options.afSetIDs.length > 0) {
      braze.logCustomEvent('Route Modifiers');
    }
  }

  trackLogout(): void {
    // braze analytics for logout
    // dummy user so that logging out and back in will update campaigns from braze
    // shouldn't be used for anything else, and should change to a real user before any data is logged
    braze.changeUser('BrazeDummyUser');
  }
}
